import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
 
@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private apiUrl = `${environment.apiBaseUrl}/users`;
 
  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private http: HttpClient
  ) {
    this.subscribeToInteractionStatus();
  }
 
  private subscribeToInteractionStatus(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.syncActiveAccount());
  }
 
  handleRedirect(): void {
    this.msalService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (result?.account) {
          this.msalService.instance.setActiveAccount(result.account);
          // Create/sync user in backend database
          this.syncUserWithBackend().then(() => {
            this.router.navigate(['/home']);
          }).catch((error) => {
            console.error('Failed to sync user with backend:', error);
            // Still navigate to home even if sync fails
            this.router.navigate(['/home']);
          });
        }
      },
      error: () => {}
    });
  }
 
  private syncUserWithBackend(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get<any>(`${this.apiUrl}/me`).subscribe({
        next: (user) => {
          console.log('User synced with backend:', user);
          resolve();
        },
        error: (error) => {
          console.error('Error syncing user with backend:', error);
          reject(error);
        }
      });
    });
  }
 
  login(): void {
    this.msalService.loginRedirect({
      scopes: environment.msalConfig.apiConfig.scopes,
    });
  }
 
  logout(): void {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: '/login',
    });
  }
 
  isLoggedIn(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }
 
  getUser(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount() ?? null;
  }
 
  getDisplayName(): string {
    const account = this.getUser();
    return account?.name || account?.username || 'User';
  }
 
  async getAccessToken(): Promise<string> {
    try {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length === 0) return '';
      const response = await this.msalService.acquireTokenSilent({
        scopes: environment.msalConfig.apiConfig.scopes,
        account: accounts[0],
      }).toPromise();
      return response?.accessToken || '';
    } catch {
      return '';
    }
  }
 
  private syncActiveAccount(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0 && !this.msalService.instance.getActiveAccount()) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }
 
  getUserRoles(): string[] {
    try {
      const account = this.getUser();
      if (!account) return [];
 
      const idTokenClaims = account.idTokenClaims as any;
      if (idTokenClaims?.roles) {
        return idTokenClaims.roles;
      }
 
      if (idTokenClaims?.appRoles) {
        return idTokenClaims.appRoles;
      }
 
      if (idTokenClaims?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) {
        const rolesClaim = idTokenClaims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        return Array.isArray(rolesClaim) ? rolesClaim : [rolesClaim];
      }
 
      return [];
    } catch (error) {
      console.log('Error extracting user roles:', error);
      return [];
    }
  }
 
  hasRole(role: string): boolean {
    const userRoles = this.getUserRoles();
    return userRoles.some(r => r.toLowerCase() === role.toLowerCase());
  }
 
  isAdmin(): boolean {
    return this.hasRole('admin') || this.hasRole('administrator');
  }
 
  isUser(): boolean {
    return this.hasRole('user');
  }
 
  hasAdminAndUserRole(): boolean {
    return this.isAdmin() && this.isUser();
  }
 
  isSidebarVisible(): boolean {
    const userRoles = this.getUserRoles().map(r => r.toLowerCase());
    return userRoles.includes('admin') || userRoles.includes('user admin');
  }
 
  getUserInfo(): any {
    const account = this.getUser();
    if (!account) return null;
 
    const idTokenClaims = account.idTokenClaims as any;
   
    return {
      id: idTokenClaims?.oid || account.localAccountId,
      oid: account.homeAccountId,
      username: account.username,
      name: account.name,
      email: idTokenClaims?.email || idTokenClaims?.preferred_username,
      ...idTokenClaims
    };
  }
 
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}